<template>
  <svg class="dial" viewBox="0 0 200 118" width="190" height="112" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <radialGradient :id="`${id}-face`" cx="50%" cy="80%" r="80%">
        <stop offset="0%" stop-color="#fbf3dc" />
        <stop offset="100%" stop-color="#e3d3a6" />
      </radialGradient>
      <linearGradient :id="`${id}-bezel`" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#e8c766" />
        <stop offset="50%" stop-color="#8a6a1f" />
        <stop offset="100%" stop-color="#d9b54f" />
      </linearGradient>
      <filter :id="`${id}-shadow`" x="-30%" y="-30%" width="160%" height="160%">
        <feDropShadow dx="0" dy="2" stdDeviation="1.5" flood-opacity="0.4" />
      </filter>
    </defs>

    <!-- Bezel and face (half disc) -->
    <path d="M 4 100 A 96 96 0 0 1 196 100 L 196 114 L 4 114 Z" :fill="`url(#${id}-bezel)`" />
    <path d="M 11 100 A 89 89 0 0 1 189 100 L 189 107 L 11 107 Z" :fill="`url(#${id}-face)`"
          stroke="#5b4510" stroke-width="1" />

    <!-- Colored zones -->
    <path v-for="z in zonePaths" :key="z.d" :d="z.d" fill="none" :stroke="z.color"
          stroke-width="6" stroke-opacity="0.8" />

    <!-- Scale arc -->
    <path :d="arc" fill="none" stroke="#3b2e10" stroke-width="1.2" />

    <!-- Ticks -->
    <g stroke="#3b2e10" stroke-linecap="round">
      <line v-for="t in tickLines" :key="t.v"
            :x1="t.x1" :y1="t.y1" :x2="t.x2" :y2="t.y2" :stroke-width="t.w" />
    </g>

    <!-- Labels -->
    <g font-size="10" font-family="Georgia, 'Times New Roman', serif" font-weight="bold"
       text-anchor="middle" dominant-baseline="central" fill="#3b2e10">
      <text v-for="l in labelPos" :key="l.v" :x="l.x" :y="l.y">{{ l.text }}</text>
    </g>
    <text x="100" y="92" font-size="9" font-family="Georgia, 'Times New Roman', serif"
          font-style="italic" text-anchor="middle" fill="#3b2e10">{{ unit }}</text>

    <!-- Needle -->
    <g class="needle" :style="`transform: rotate(${needleAngle}deg);`">
      <g :filter="`url(#${id}-shadow)`">
        <polygon points="100,28 103.5,100 100,106 96.5,100" fill="#8e1b1b" />
        <polygon points="100,28 100,106 96.5,100" fill="#fff" fill-opacity="0.2" />
      </g>
    </g>
    <circle cx="100" cy="100" r="6" :fill="`url(#${id}-bezel)`" stroke="#5b4510" stroke-width="1" />
    <circle cx="100" cy="100" r="2" fill="#3b2e10" />
  </svg>
</template>

<script>
const START = 270   // degrees clockwise from top: scale runs over the top from left to right
const SPAN = 180
const CX = 100
const CY = 100

// angle (deg clockwise from top) -> point on circle of radius r
function polar(angle, r) {
  const rad = angle * Math.PI / 180
  return { x: CX + r * Math.sin(rad), y: CY - r * Math.cos(rad) }
}

function arcPath(a0, a1, r) {
  const p0 = polar(a0, r)
  const p1 = polar(a1, r)
  const large = a1 - a0 > 180 ? 1 : 0
  return `M ${p0.x} ${p0.y} A ${r} ${r} 0 ${large} 1 ${p1.x} ${p1.y}`
}

export default {
  name: 'SemiGauge',
  props: {
    id: { type: String, required: true },     // unique prefix for SVG ids
    value: Number,
    min: { type: Number, required: true },
    max: { type: Number, required: true },
    log: { type: Boolean, default: false },   // logarithmic scale (min must be > 0)
    unit: String,
    zones: { type: Array, default: () => [] }, // [from, to, color]
    ticks: { type: Array, default: () => [] }  // { v, size: 'major'|'mid'|'minor', label? }
  },
  computed: {
    needleAngle() {
      const v = Number.isFinite(this.value) ? this.value : this.min
      return this.valueToAngle(v)
    },
    arc() {
      return arcPath(START, START + SPAN, 78)
    },
    zonePaths() {
      return this.zones.map(([from, to, color]) => ({
        d: arcPath(this.valueToAngle(from), this.valueToAngle(to), 80),
        color
      }))
    },
    tickLines() {
      const len = { major: 10, mid: 7, minor: 4 }
      const w = { major: 1.8, mid: 1.2, minor: 0.7 }
      return this.ticks.map(t => {
        const a = this.valueToAngle(t.v)
        const p1 = polar(a, 78)
        const p2 = polar(a, 78 - len[t.size || 'minor'])
        return { v: t.v, x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, w: w[t.size || 'minor'] }
      })
    },
    labelPos() {
      return this.ticks.filter(t => t.label !== undefined).map(t => {
        const p = polar(this.valueToAngle(t.v), 60)
        return { v: t.v, text: t.label, x: p.x, y: p.y }
      })
    }
  },
  methods: {
    valueToAngle(v) {
      const c = Math.min(this.max, Math.max(this.min, v))
      const t = this.log
        ? (Math.log10(c) - Math.log10(this.min)) / (Math.log10(this.max) - Math.log10(this.min))
        : (c - this.min) / (this.max - this.min)
      return START + t * SPAN
    }
  }
}
</script>

<style scoped>
.dial {
  display: block;
  margin: 10px auto 0;
}

.needle {
  transform-origin: 100px 100px;
  transition: transform 1.2s cubic-bezier(0.34, 1.3, 0.5, 1);
}
</style>
